import React from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { captureException } from '../lib/sentry/sentry-utils';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches React errors and reports them to Sentry
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error);
      console.error('Error info:', errorInfo);
    }

    // Capture error to Sentry with full context
    captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'true',
        errorBoundaryName: this.constructor.name,
      },
      extra: {
        errorInfo,
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });

    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount(): void {
    this.setState({
      hasError: false,
      error: null,
    });
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * Default error fallback component
 */
function DefaultErrorFallback({ error, resetError }: FallbackProps) {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
              <p className="text-sm text-gray-600">An unexpected error occurred in the application</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900">Error Details</h3>
                <p className="mt-2 font-mono text-sm text-red-800 break-all">{error.message}</p>
              </div>
            </div>
          </div>

          {import.meta.env.DEV && error.stack && (
            <details className="rounded-lg border border-gray-200 p-4 bg-gray-50">
              <summary className="cursor-pointer font-medium text-sm text-gray-700">
                Stack Trace (Development Only)
              </summary>
              <pre className="mt-3 overflow-auto text-xs text-gray-600 whitespace-pre-wrap break-all">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={resetError}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCcw className="h-4 w-4" />
              Try Again
            </button>
            <button
              onClick={handleGoHome}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Home className="h-4 w-4" />
              Go to Dashboard
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            If this problem persists, please contact support.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
